/**
 * Category (scope) multi-select filter — iter305.
 *
 * Same field on both lists: proposals carry `scopes[].name` (the section
 * header on the PDF), work orders carry `wo_scopes[]`. A record with
 * lines in two categories matches either selection.
 *
 * Only values actually in use are offered, the count is on the button,
 * and the choice is remembered per browser under `storageKey`.
 */
import { useEffect, useMemo, useState } from "react";
import { Check, X, ListFilter, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/** Category names on one record, from either shape. */
export const recordCategories = (rec) => {
  const raw = rec?.wo_scopes?.length ? rec.wo_scopes : (rec?.scopes || []);
  return raw
    .map((s) => (typeof s === "string" ? s : s?.name || s?.scope_name || ""))
    .map((s) => s.trim())
    .filter(Boolean);
};

export const matchesCategories = (rec, selected) => {
  if (!selected?.length) return true;
  const mine = recordCategories(rec);
  return mine.some((c) => selected.includes(c));
};

export function useStickyCategories(storageKey) {
  const [value, setValue] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || "[]"); } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(value)); } catch { /* private mode */ }
  }, [storageKey, value]);
  return [value, setValue];
}

export function CategoryFilter({ records = [], value = [], onChange, testId = "category-filter",
                                 className = "", compact = false, iconOnly = false, pill = false, label = "category" }) {
  const options = useMemo(() => {
    const counts = new Map();
    records.forEach((r) => recordCategories(r).forEach((c) => counts.set(c, (counts.get(c) || 0) + 1)));
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [records]);

  const toggle = (name) =>
    onChange(value.includes(name) ? value.filter((v) => v !== name) : [...value, name]);

  if (!options.length && !value.length) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        {iconOnly ? (
          // Column-header mode: the small filter icon beside the column name (UI sweep rule).
          <button type="button" title={`Filter by ${label}`} onClick={(e) => e.stopPropagation()} data-testid={testId}
            className={`rounded p-0.5 hover:bg-slate-100 ${value.length ? "text-brand" : "text-slate-400 hover:text-slate-700"}`}>
            <ListFilter className="h-3 w-3" />
            {value.length > 0 && <span className="sr-only" data-testid={`${testId}-count`}>{value.length}</span>}
          </button>
        ) : pill ? (
          // Pill-rail mode: sits inside a PillRail and looks like the other pills; opens the same dropdown.
          <button type="button" data-testid={testId} aria-pressed={value.length > 0} title={`Filter by ${label}`}
            className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${value.length ? "seg-on" : "seg-off"} ${className}`}>
            {value.length === 1 ? value[0] : value.length > 1 ? `${value.length} Categories` : "Category"}
            <ChevronDown className="h-3 w-3 opacity-70" />
            {value.length > 0 && <span className="sr-only" data-testid={`${testId}-count`}>{value.length}</span>}
          </button>
        ) : (
        <button
          type="button"
          className={`${compact ? "h-8 text-xs px-1.5" : "h-9 text-xs px-3"} inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white text-slate-700 outline-none transition-colors hover:bg-slate-50 focus:border-brand ${className}`}
          data-testid={testId}
        >
          <span className="flex-1 text-left truncate">{value.length === 1 ? value[0] : value.length > 1 ? `${value.length} categories` : "Category"}</span>
          {value.length > 0 && (
            <span className="px-1.5 rounded-full bg-slate-900 text-white text-[10px] font-semibold"
                  data-testid={`${testId}-count`}>
              {value.length}
            </span>
          )}
          <ChevronDown className="h-3 w-3 opacity-60 shrink-0" />
        </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1.5 font-normal normal-case tracking-normal" data-testid={`${testId}-menu`}>
        <div className="flex items-center justify-between px-1.5 py-1">
          <span className="text-[11px] font-semibold text-slate-500">
            Categories in use
          </span>
          {value.length > 0 && (
            <button type="button" onClick={() => onChange([])}
                    className="text-[10px] text-slate-500 hover:text-slate-800 flex items-center gap-0.5"
                    data-testid={`${testId}-clear`}>
              <X className="w-2.5 h-2.5" />clear
            </button>
          )}
        </div>
        <div className="max-h-64 overflow-y-auto">
          {options.length === 0 && (
            <div className="px-2 py-2 text-xs text-slate-400">No categories on these records yet</div>
          )}
          {options.map(([name, count]) => {
            const on = value.includes(name);
            return (
              <button
                key={name}
                type="button"
                onClick={() => toggle(name)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-xs transition-colors ${
                  on ? "bg-slate-900 text-white" : "hover:bg-slate-100 text-slate-700"}`}
                data-testid={`${testId}-option-${name.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                  on ? "border-white/60" : "border-slate-300"}`}>
                  {on && <Check className="w-2.5 h-2.5" />}
                </span>
                <span className="flex-1 truncate">{name}</span>
                <span className={on ? "text-white/60 text-[10px]" : "text-slate-400 text-[10px]"}>{count}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
